import { z } from 'zod';
import {
  AudioBinarioMimeTypeSchema,
  FORMATO_UPLINK,
  TIPO_INTERFAZ_CONECTAR,
  type WS_CLIENT_TYPE,
} from './common.js';
import { AnimacionBaseSchema } from './contexto.js';
import { SesionErrorCodigoSchema } from './estados.js';
import { ExpresionVRLaxoSchema } from './expresiones.js';

/**
 * Wire literal del WebSocket según `docs/backend/JSON_CONTRACTS.md` v1.9.0
 * (`JC`). Todo en snake_case (JC §0.1), sin lectura tolerante de otras
 * grafías: el cliente emite y espera una sola forma.
 *
 * Los schemas de lo que **llega** del servidor no son estrictos: campos no
 * declarados se descartan en el borde sin error (D-5). En particular,
 * `ia.behavioral_cues` **no se declara**: llega hoy al destinatario
 * `webxr`, va a dejar de llegar, y no debe pasar del borde.
 *
 * El remapeo `expresion_vr → expresion_avatar` no vive acá: se aplica en
 * `@alter/api-client`.
 */

// ==============================
// Handshake (JC §0.4)
// ==============================

/**
 * Objeto `auth` del handshake de Socket.IO. `token` va **pelado**, sin
 * `Bearer`. `client_type` es siempre `webxr` para este cliente: es el dato
 * que selecciona la fila `webxr` de la matriz de filtrado (JC §3.2).
 */
export interface WsHandshakeAuth {
  token: string;
  client_type: typeof WS_CLIENT_TYPE;
}

// ==============================
// Cliente → servidor
// ==============================

const ConectarComunSchema = z.object({
  evento: z.literal('conectar'),
  tenant_id: z.string(),
  /** Minúsculas en el wire (`text_to_speech`). */
  modalidad: z.string(),
  tipo_interfaz: z.literal(TIPO_INTERFAZ_CONECTAR),
});

/**
 * `conectar` (JC §0.3). Dos formas excluyentes:
 *
 * - **Primera conexión**: con `ticket_ref` (one-time, TTL 5 min; se quema
 *   en el primer `conectar` exitoso).
 * - **Reconexión**: con `simulacion_id` y **sin** `ticket_ref`, porque el
 *   ticket ya se quemó.
 */
export const WsConectarPayloadSchema = z.union([
  ConectarComunSchema.extend({ ticket_ref: z.string() }).strict(),
  ConectarComunSchema.extend({ simulacion_id: z.string() }).strict(),
]);
export type WsConectarPayload = z.infer<typeof WsConectarPayloadSchema>;

/**
 * Metadata de `audio.generado_por_alumno` (JC §2; nota de interfaz §3.1).
 * `muestra_hz` se omite: el servidor no lo lee y el `16000` de JC §2 es de VR.
 *
 * **El turno viaja como UN solo objeto** con el binario en el campo `audio`
 * (ver `WsAudioGeneradoPorAlumnoBinario`). Verificado en el gateway el
 * 2026-09-19 (`sesion.gateway.ts:784`, `:803`): el handler lee un único
 * `@MessageBody()`. Si el `emit` lleva la metadata y el binario como dos
 * argumentos, NestJS entrega un array, `data.audio` queda vacío y el turno
 * falla con "file is empty". JC §2 no nombra el campo, y su "frame JSON +
 * frame binario separados" induce a ese error (hueco en `02_CONTRATOS.md` §6).
 *
 * **El evento no tiene ack**: no se pasa callback. La señal de que el turno
 * entró es `paciente.hablando_inicio`.
 */
export const WsAudioGeneradoPorAlumnoPayloadSchema = z.object({
  evento: z.literal('audio.generado_por_alumno'),
  simulacion_id: z.string(),
  secuencia: z.number(),
  audio_ref_id: z.string(),
  /** `0` mientras el cliente no mida silencio. Solo se persiste. */
  silencio_previo_segundos: z.number(),
  /** Medida real: alimenta el filtro de audios cortos del STT. */
  duracion_audio_segundos: z.number(),
  formato: z.literal(FORMATO_UPLINK),
  /** El `type` real del blob grabado. Respaldo de `formato`. */
  mime_type: z.string(),
  /** RMS medido en el cliente (descarte de silencio, D-7). Opcional; lo acepta el gateway. */
  rms_cliente: z.number().optional(),
});
export type WsAudioGeneradoPorAlumnoPayload = z.infer<typeof WsAudioGeneradoPorAlumnoPayloadSchema>;

/**
 * Binario del audio del alumno, en el campo `audio` del mismo objeto.
 * `ArrayBuffer` crudo, sin validación Zod: Socket.IO lo manda como
 * attachment binario (no base64) y el gateway lo normaliza a `Buffer`. Tope
 * del servidor: 5 MB por frame (JC §2); excederlo llega como `disconnect`
 * con `reason: "transport close"`, sin evento de aplicación.
 */
export type WsAudioGeneradoPorAlumnoBinario = ArrayBuffer;

/** Lo que efectivamente se emite: la metadata y el binario en un solo objeto. */
export type WsAudioGeneradoPorAlumnoWire = WsAudioGeneradoPorAlumnoPayload & {
  audio: WsAudioGeneradoPorAlumnoBinario;
};

// ==============================
// Servidor → cliente
// ==============================

/** Ack del `conectar` (JC §0.3). */
export const WsConectarAckSchema = z.object({
  ok: z.literal(true),
  simulacion_id: z.string(),
  reconexion: z.boolean(),
});
export type WsConectarAck = z.infer<typeof WsConectarAckSchema>;

/**
 * Lo que el destinatario `webxr` puede usar del payload v3 filtrado
 * (JC §3.1–3.2). `behavioral_cues` llega pero no se declara (D-5).
 */
export const WsIaWebxrSchema = z.object({
  patient_response: z.string(),
  expresion_vr: ExpresionVRLaxoSchema,
});
export type WsIaWebxr = z.infer<typeof WsIaWebxrSchema>;

/**
 * Envelope de `audio.generado_por_paciente` (JC §3.3). Llega **antes** del
 * frame `audio.binario`; en `text_to_speech` llega exactamente un frame
 * binario por envelope, correlacionado por `audio_ref_id`.
 *
 * El payload v3 no trae ninguna intensidad para este destinatario.
 */
export const WsAudioGeneradoPorPacientePayloadSchema = z.object({
  simulacion_id: z.string(),
  secuencia: z.number(),
  audio_ref_id: z.string(),
  duracion_audio_segundos: z.number(),
  /** Descriptivo, sin enum. Ramificar por el `mime_type` del binario. */
  formato: z.string(),
  alumno_transcripcion: z.string(),
  ia: WsIaWebxrSchema,
});
export type WsAudioGeneradoPorPacientePayload = z.infer<
  typeof WsAudioGeneradoPorPacientePayloadSchema
>;

/**
 * Frame `audio.binario` (JC §0.2). `data` no se valida con Zod; el
 * `mime_type` se valida como string y el reproductor decide si puede
 * reproducirlo. Para este cliente el servidor manda `audio/mpeg`.
 */
export const WsAudioBinarioMetaSchema = z.object({
  mime_type: AudioBinarioMimeTypeSchema,
});
export interface WsAudioBinarioPayload {
  data: ArrayBuffer;
  mime_type: string;
}

/**
 * Backchannel del paciente (JC §4): sonido no verbal corto, seguido de su
 * frame `audio.binario`. `payload.avatar` es opcional y `animacion_base`
 * laxo; no se renderiza.
 */
export const WsAudioBackchannelGeneradoPorPacientePayloadSchema = z.object({
  simulacion_id: z.string(),
  secuencia: z.number(),
  audio_ref_id: z.string(),
  payload: z.object({
    tipo_backchannel: z.string(),
    descripcion: z.string(),
    avatar: z
      .object({
        animacion_base: AnimacionBaseSchema,
        duracion_ms: z.number(),
      })
      .optional(),
  }),
});
export type WsAudioBackchannelGeneradoPorPacientePayload = z.infer<
  typeof WsAudioBackchannelGeneradoPorPacientePayloadSchema
>;

/**
 * Turn-taking (JC §6). Entre `_inicio` y `_fin` el micrófono está pausado.
 * `_fin` es la señal de re-armado y llega **también** cuando el turno
 * falla, antes de `turno.fallido`.
 */
export const WsPacienteHablandoInicioPayloadSchema = z.object({
  simulacion_id: z.string(),
});
export type WsPacienteHablandoInicioPayload = z.infer<typeof WsPacienteHablandoInicioPayloadSchema>;

export const WsPacienteHablandoFinPayloadSchema = z.object({
  simulacion_id: z.string(),
});
export type WsPacienteHablandoFinPayload = z.infer<typeof WsPacienteHablandoFinPayloadSchema>;

/** Único valor del catálogo de `turno.fallido` (JC §6.2). */
export const TURNO_FALLIDO_CODIGO = 'turno_fallido' as const;

/**
 * Falla del pipeline en **este** turno (JC §6.2). No cierra el socket ni la
 * simulación: el cliente avisa y el alumno repite el turno. Reconectar acá
 * es un bug. `codigo` es laxo para que una categoría futura no se pierda.
 */
export const WsTurnoFallidoPayloadSchema = z.object({
  simulacion_id: z.string(),
  codigo: z.string(),
});
export type WsTurnoFallidoPayload = z.infer<typeof WsTurnoFallidoPayloadSchema>;

/**
 * Error fatal de sesión (JC §0.5): tras cualquiera, el servidor cierra el
 * socket. Narrow con `isSesionErrorCodigoConocido`. `mensaje` es
 * orientativo y el cliente no lo muestra, así que su ausencia no invalida
 * el evento.
 */
export const WsSesionErrorPayloadSchema = z.object({
  codigo: SesionErrorCodigoSchema,
  mensaje: z.string().optional(),
});
export type WsSesionErrorPayload = z.infer<typeof WsSesionErrorPayloadSchema>;

// ==============================
// Nombres literales de evento
// ==============================

/**
 * Nombres de evento Socket.IO tal como los emite/consume el gateway.
 * Fuente: JC. Fuera de alcance del taller y sin entrada acá: despedida
 * (§6.1) y cierres de modalidad `multimodal` (§6.3).
 */
export const WS_EVENTS = {
  // Cliente → servidor
  conectar: 'conectar',
  audio_generado_por_alumno: 'audio.generado_por_alumno',
  entrevista_finalizada_por_alumno: 'entrevista.finalizada_por_alumno',
  sesion_finalizada_por_alumno: 'sesion.finalizada_por_alumno',
  // Servidor → cliente
  audio_generado_por_paciente: 'audio.generado_por_paciente',
  audio_binario: 'audio.binario',
  audio_backchannel_generado_por_paciente: 'audio.backchannel_generado_por_paciente',
  paciente_hablando_inicio: 'paciente.hablando_inicio',
  paciente_hablando_fin: 'paciente.hablando_fin',
  turno_fallido: 'turno.fallido',
  sesion_error: 'sesion.error',
} as const;

export type WsEventName = (typeof WS_EVENTS)[keyof typeof WS_EVENTS];
